/**
 * @fileoverview Firm billing manage dialog (MOR-438)
 *
 * One firm end to end: the charge ledger (with manual charge entry), the
 * affiliate fees its revenue accrues, and the plan + Stripe wiring. Opened
 * from the dashboard, the Firms tab and the payment-status drill-down.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useMutation } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/ui/composed/data-display/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Skeleton } from "@/ui/primitives/skeleton";
import { Textarea } from "@/ui/primitives/textarea";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { toast } from "sonner";
import {
  AlertTriangle,
  Ban,
  Check,
  ExternalLink,
  Inbox,
  ReceiptText,
} from "lucide-react";
import { AffiliateFeeTable } from "./AffiliateFeeTable";
import { adjustmentExplainer } from "./affiliateCopy";
import {
  centsToDollarsInput,
  formatAudExact,
  formatDate,
  formatPeriod,
  parseDollarsToCents,
  shortMonth,
  type Agreement,
  type BillingPlan,
  type OrgDetail,
} from "./billingFormat";
import {
  ChargeStatusPill,
  EmptyState,
  PlanBadge,
  StatTile,
  StripeStatusPill,
} from "./billingShared";

const firmRevenueChartConfig = {
  subscriptionAudCents: {
    label: "Subscription",
    color: "hsl(var(--chart-2))",
  },
  perJobAudCents: { label: "Per job", color: "hsl(var(--chart-1))" },
} satisfies ChartConfig;

export function ManageOrgDialog({
  orgId,
  onClose,
}: {
  orgId: Id<"organizations">;
  onClose: () => void;
}) {
  const { data: detail } = useQuery(
    convexQuery(api.admin.billing.getOrgBillingDetail, { orgId }),
  );
  // Agreements come from the overview query so the dialog can be opened from
  // either tab (the dashboard doesn't load them itself).
  const { data: overview } = useQuery(
    convexQuery(api.admin.billing.getBillingOverview, {}),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] min-w-0 overflow-y-auto overflow-x-hidden sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{detail?.orgName ?? "Firm billing"}</DialogTitle>
          {detail ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <PlanBadge plan={detail.plan} />
              <StripeStatusPill
                stripeCustomerId={detail.stripeCustomerId}
                stripeSubscriptionStatus={detail.stripeSubscriptionStatus}
              />
              {detail.convertedAt && (
                <span className="text-xs text-muted-foreground">
                  converted {formatDate(detail.convertedAt)}
                </span>
              )}
              {detail.affiliate && (
                <span className="text-xs text-muted-foreground">
                  · affiliate: {detail.affiliate.agreementName}
                </span>
              )}
              {detail.stripeDashboardUrl && detail.stripeCustomerId && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  asChild
                >
                  <a
                    href={`${detail.stripeDashboardUrl}/customers/${detail.stripeCustomerId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Stripe
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </Button>
              )}
            </div>
          ) : (
            <DialogDescription>
              Billing plan, Stripe wiring and full charge history for this firm.
            </DialogDescription>
          )}
        </DialogHeader>

        {!detail ? (
          <div className="flex flex-col gap-3 py-2">
            <Skeleton className="h-10 rounded-lg" />
            <Skeleton className="h-24 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : (
          <Tabs defaultValue="charges" className="flex min-w-0 flex-col gap-5">
            <TabsList className="self-start">
              <TabsTrigger value="charges">Charges</TabsTrigger>
              <TabsTrigger value="affiliate">
                Affiliate
                {detail.affiliate &&
                  detail.affiliate.subtotals.feeAudCents > 0 && (
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {formatAudExact(detail.affiliate.subtotals.feeAudCents)}
                    </span>
                  )}
              </TabsTrigger>
              <TabsTrigger value="plan">Plan &amp; Stripe</TabsTrigger>
            </TabsList>

            <TabsContent
              value="charges"
              className="mt-0 flex min-w-0 flex-col gap-6"
            >
              <FirmTotals totals={detail.totals} />
              <FirmRevenueChart byMonth={detail.byMonth} />
              <ChargesTable detail={detail} />
              <RecordChargeForm orgId={orgId} detail={detail} />
            </TabsContent>

            <TabsContent
              value="affiliate"
              className="mt-0 flex min-w-0 flex-col gap-4"
            >
              <FirmAffiliateSection affiliate={detail.affiliate} />
            </TabsContent>

            <TabsContent
              value="plan"
              className="mt-0 flex min-w-0 flex-col gap-5"
            >
              <PlanForm
                orgId={orgId}
                detail={detail}
                agreements={overview?.agreements ?? []}
              />
              <StripeSection orgId={orgId} detail={detail} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Charges tab
// ---------------------------------------------------------------------------

/** Per-firm headline numbers above the charge history. */
function FirmTotals({ totals }: { totals: OrgDetail["totals"] }) {
  const collectionPct =
    totals.billedExGstAudCents > 0
      ? Math.round(
          (totals.paidExGstAudCents / totals.billedExGstAudCents) * 100,
        )
      : 0;
  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      <StatTile
        label="Billed"
        value={formatAudExact(totals.billedExGstAudCents)}
        hint="Ex GST"
      />
      <StatTile
        label="Collected"
        value={formatAudExact(totals.paidExGstAudCents)}
        hint={`${collectionPct}% of billed, ex GST`}
      >
        <div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-emerald-500"
            style={{ width: `${collectionPct}%` }}
          />
        </div>
      </StatTile>
      <StatTile
        label="Outstanding"
        value={formatAudExact(totals.outstandingAudCents)}
        hint="Inc GST"
      />
      <StatTile
        label="Overdue"
        value={formatAudExact(totals.overdueAudCents)}
        hint="Inc GST"
        tone={totals.overdueAudCents > 0 ? "warning" : "default"}
      />
    </div>
  );
}

/** Ex-GST billed per month for this firm, split by fee type. */
function FirmRevenueChart({ byMonth }: { byMonth: OrgDetail["byMonth"] }) {
  if (byMonth.length === 0) return null;
  const chartData = byMonth.map((m) => ({
    month: shortMonth(m.periodYearMonth),
    subscriptionAudCents: m.subscriptionAudCents,
    perJobAudCents: m.perJobAudCents,
  }));
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">Billed by month</h3>
      <ChartContainer
        config={firmRevenueChartConfig}
        className="h-[180px] w-full"
      >
        <BarChart data={chartData} accessibilityLayer maxBarSize={56}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            width={64}
            tickFormatter={(v: number) => formatAudExact(v)}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                formatter={(value) => formatAudExact(Number(value))}
              />
            }
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar
            dataKey="subscriptionAudCents"
            stackId="revenue"
            fill="var(--color-subscriptionAudCents)"
            stroke="hsl(var(--background))"
            strokeWidth={2}
          />
          <Bar
            dataKey="perJobAudCents"
            stackId="revenue"
            fill="var(--color-perJobAudCents)"
            stroke="hsl(var(--background))"
            strokeWidth={2}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ChartContainer>
    </div>
  );
}

function ChargesTable({ detail }: { detail: OrgDetail }) {
  const [workingId, setWorkingId] = useState<string | null>(null);
  const setChargeStatus = useMutation(api.admin.billing.setChargeStatus);
  const sendChargeInvoice = useAction(
    api.admin.billingStripe.sendChargeInvoice,
  );

  const handleSendInvoice = async (chargeId: Id<"billingCharges">) => {
    setWorkingId(chargeId);
    try {
      await sendChargeInvoice({ chargeId });
      toast.success("Invoice sent via Stripe.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to send invoice."));
    } finally {
      setWorkingId(null);
    }
  };

  const handleStatus = async (
    chargeId: Id<"billingCharges">,
    status: "draft" | "invoiced" | "paid" | "void",
  ) => {
    setWorkingId(chargeId);
    try {
      await setChargeStatus({ chargeId, status });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to update charge."));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <section className="flex min-w-0 flex-col gap-2">
      <h3 className="text-sm font-semibold">Charge history</h3>
      <div className="min-w-0 overflow-x-auto rounded-lg border">
        {/* Fixed floor width: the ledger scrolls sideways rather than
            crushing its columns inside the dialog. */}
        <Table containerClassName="min-w-0" className="min-w-[820px]">
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead>Period</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Ex GST</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Due</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.charges.map((charge) => (
              <TableRow key={charge._id}>
                <TableCell className="whitespace-nowrap text-sm">
                  {formatPeriod(charge.periodYearMonth)}
                </TableCell>
                <TableCell className="max-w-[220px]">
                  <div className="truncate text-sm">{charge.description}</div>
                  {charge.jobLabel && (
                    <div className="truncate text-xs text-muted-foreground">
                      {charge.jobLabel}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatAudExact(charge.amountExGstAudCents)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {formatAudExact(charge.gstAudCents)}
                </TableCell>
                <TableCell className="text-right font-medium tabular-nums">
                  {formatAudExact(charge.totalAudCents)}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <ChargeStatusPill
                      status={charge.overdue ? "overdue" : charge.status}
                    />
                    {charge.stripeStatus &&
                      charge.stripeStatus !== charge.status && (
                        <span className="text-[10px] text-muted-foreground">
                          Stripe: {charge.stripeStatus}
                        </span>
                      )}
                  </div>
                </TableCell>
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                  {charge.paidAt
                    ? `paid ${formatDate(charge.paidAt)}`
                    : charge.dueDate
                      ? formatDate(charge.dueDate)
                      : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {/* Icon actions with title tooltips — text buttons made the
                      row overflow its container. */}
                  <div className="flex items-center justify-end gap-0.5 whitespace-nowrap">
                    {detail.stripeDashboardUrl && charge.stripeInvoiceId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        asChild
                      >
                        <a
                          href={`${detail.stripeDashboardUrl}/invoices/${charge.stripeInvoiceId}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Open invoice in Stripe dashboard"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    )}
                    {charge.hostedInvoiceUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        asChild
                      >
                        <a
                          href={charge.hostedInvoiceUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open hosted invoice (what the firm sees)"
                        >
                          <ReceiptText className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    )}
                    {charge.status === "draft" && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        disabled={workingId === charge._id}
                        onClick={() => handleSendInvoice(charge._id)}
                      >
                        Invoice
                      </Button>
                    )}
                    {(charge.status === "draft" ||
                      charge.status === "invoiced") && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Mark paid"
                        disabled={workingId === charge._id}
                        onClick={() => handleStatus(charge._id, "paid")}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {charge.status === "draft" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground"
                        title="Void charge"
                        disabled={workingId === charge._id}
                        onClick={() => handleStatus(charge._id, "void")}
                      >
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {detail.charges.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <EmptyState
                    icon={<Inbox className="h-8 w-8" />}
                    title="No charges recorded yet"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function RecordChargeForm({
  orgId,
  detail,
}: {
  orgId: Id<"organizations">;
  detail: OrgDetail;
}) {
  const [kind, setKind] = useState<"subscription" | "per_job">("per_job");
  const [jobId, setJobId] = useState<string>("none");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const recordCharge = useMutation(api.admin.billing.recordCharge);

  const handleRecord = async () => {
    const cents = parseDollarsToCents(amount);
    if (cents === null) {
      toast.error("Enter a positive amount.");
      return;
    }
    if (!description.trim()) {
      toast.error("Enter a description.");
      return;
    }
    setIsSaving(true);
    try {
      await recordCharge({
        orgId,
        kind,
        jobId: jobId === "none" ? undefined : (jobId as Id<"jobs">),
        description: description.trim(),
        amountExGstAudCents: cents,
      });
      toast.success("Charge recorded as draft.");
      setAmount("");
      setDescription("");
      setJobId("none");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to record charge."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-dashed bg-muted/20 p-4">
      <div className="flex items-center gap-2">
        <ReceiptText className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Record a manual charge</h3>
        <span className="text-xs text-muted-foreground">
          Saved as a draft — invoice it from the ledger above.
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">Type</Label>
          <Select
            value={kind}
            onValueChange={(v) => setKind(v as "subscription" | "per_job")}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="per_job">Per-job fee</SelectItem>
              <SelectItem value="subscription">Subscription fee</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {kind === "per_job" && (
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">Job</Label>
            <Select value={jobId} onValueChange={setJobId}>
              <SelectTrigger>
                <SelectValue placeholder="Job" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No job</SelectItem>
                {detail.jobs.map((j) => (
                  <SelectItem key={j.jobId} value={j.jobId}>
                    {j.jobCode ? `${j.jobCode} — ${j.label}` : j.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs" htmlFor="charge-amount">
            Amount (AUD, ex GST)
          </Label>
          <Input
            id="charge-amount"
            type="number"
            min="0"
            step="0.01"
            placeholder="2000"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs" htmlFor="charge-description">
            Description
          </Label>
          <Input
            id="charge-description"
            placeholder="e.g. Complex CVL — Acme Pty Ltd"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRecord}
          disabled={isSaving}
        >
          {isSaving ? "Recording…" : "Record draft charge"}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Affiliate tab
// ---------------------------------------------------------------------------

/**
 * What this firm's revenue costs us in affiliate fees. Same figures as the
 * per-agreement breakdown — both come from `computeAffiliateFeeRows`.
 */
function FirmAffiliateSection({
  affiliate,
}: {
  affiliate: OrgDetail["affiliate"];
}) {
  if (!affiliate) {
    return (
      <EmptyState
        title="Not linked to an affiliate agreement"
        hint="No fees accrue on this firm's revenue. Link an agreement under Plan & Stripe."
      />
    );
  }

  const { subtotals } = affiliate;
  const netAudCents = subtotals.grossAudCents - subtotals.feeAudCents;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl border bg-muted/30 p-4">
        <div className="text-sm font-semibold">{affiliate.agreementName}</div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          {adjustmentExplainer(affiliate.grossMarginAdjustment)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          label="Gross"
          value={formatAudExact(subtotals.grossAudCents)}
          hint="Ex GST"
        />
        <StatTile
          label="Adjustment"
          value={
            subtotals.adjustmentAudCents > 0
              ? `−${formatAudExact(subtotals.adjustmentAudCents)}`
              : "—"
          }
        />
        <StatTile
          label="Affiliate fee"
          value={formatAudExact(subtotals.feeAudCents)}
        />
        <StatTile
          label="Net to Morgan"
          value={formatAudExact(netAudCents)}
          tone="positive"
        />
      </div>

      {affiliate.churned && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Churn invalidation has triggered — six consecutive zero-revenue
            months. No further fee accrues until the firm re-qualifies (reset
            its conversion date).
          </span>
        </div>
      )}

      <AffiliateFeeTable rows={affiliate.rows} subtotals={subtotals} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plan & Stripe tab
// ---------------------------------------------------------------------------

function PlanForm({
  orgId,
  detail,
  agreements,
}: {
  orgId: Id<"organizations">;
  detail: OrgDetail;
  agreements: Agreement[];
}) {
  const [plan, setPlan] = useState<BillingPlan>(detail.plan);
  const [monthlyFee, setMonthlyFee] = useState(
    centsToDollarsInput(detail.subscriptionMonthlyExGstAudCents),
  );
  const [convertedDate, setConvertedDate] = useState(
    detail.convertedAt
      ? new Date(detail.convertedAt).toISOString().slice(0, 10)
      : "",
  );
  const [billingEmail, setBillingEmail] = useState(detail.billingEmail ?? "");
  const [notes, setNotes] = useState(detail.notes ?? "");
  const [agreementId, setAgreementId] = useState<string>(
    detail.affiliateAgreementId ?? "none",
  );
  const [isSaving, setIsSaving] = useState(false);

  const setOrgBillingPlan = useMutation(api.admin.billing.setOrgBillingPlan);
  const assignAgreement = useMutation(
    api.admin.billing.assignAffiliateAgreement,
  );

  const handleSave = async () => {
    const monthlyCents = parseDollarsToCents(monthlyFee);
    if (plan === "subscription" && monthlyCents === null) {
      toast.error("Enter a monthly fee for the subscription plan.");
      return;
    }
    setIsSaving(true);
    try {
      await setOrgBillingPlan({
        orgId,
        plan,
        subscriptionMonthlyExGstAudCents: monthlyCents ?? undefined,
        convertedAt:
          plan !== "pilot" && convertedDate
            ? Date.parse(`${convertedDate}T00:00:00Z`)
            : undefined,
        billingEmail: billingEmail.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      const newAgreementId =
        agreementId === "none"
          ? null
          : (agreementId as Id<"affiliateAgreements">);
      if (newAgreementId !== detail.affiliateAgreementId) {
        await assignAgreement({ orgId, agreementId: newAgreementId });
      }
      toast.success("Billing plan updated.");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to update billing plan."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="flex flex-col gap-4 rounded-xl border p-4">
      <h3 className="text-sm font-semibold">Plan</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <Label>Billing plan</Label>
          <Select value={plan} onValueChange={(v) => setPlan(v as BillingPlan)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pilot">Pilot Program (not charged)</SelectItem>
              <SelectItem value="subscription">
                Subscription (monthly + GST)
              </SelectItem>
              <SelectItem value="per_job">Per Job (+ GST)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {plan === "subscription" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="monthly-fee">Monthly fee (AUD, ex GST)</Label>
            <Input
              id="monthly-fee"
              type="number"
              min="0"
              step="0.01"
              placeholder="5000"
              value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)}
            />
          </div>
        )}
        {plan !== "pilot" && (
          <div className="flex flex-col gap-2">
            <Label htmlFor="converted-date">
              Conversion date (affiliate fee clock)
            </Label>
            <Input
              id="converted-date"
              type="date"
              value={convertedDate}
              onChange={(e) => setConvertedDate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use today when first moving off pilot.
            </p>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Label htmlFor="billing-email">Billing email</Label>
          <Input
            id="billing-email"
            type="email"
            placeholder="accounts@firm.com.au"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Affiliate agreement</Label>
          <Select value={agreementId} onValueChange={setAgreementId}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {agreements
                .filter((a) => !a.archived)
                .map((a) => (
                  <SelectItem key={a._id} value={a._id}>
                    {a.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <Label htmlFor="billing-notes">Notes</Label>
          <Textarea
            id="billing-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
      <div>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving…" : "Save plan"}
        </Button>
      </div>
    </section>
  );
}

/** Mono Stripe ID chip; links into the Stripe dashboard when it can. */
function StripeIdChip({
  label,
  id,
  href,
}: {
  label: string;
  id: string;
  href: string | null;
}) {
  const chip = (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono">{id}</code>
  );
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-muted-foreground">{label}</span>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 hover:underline"
          title={`Open ${label.toLowerCase()} in Stripe dashboard`}
        >
          {chip}
          <ExternalLink className="h-3 w-3 text-muted-foreground" />
        </a>
      ) : (
        chip
      )}
    </span>
  );
}

/** Small ordered-step marker; ticks once the step is complete. */
function StepNumber({ n, done }: { n: number; done: boolean }) {
  return (
    <span
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-medium",
        done
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground",
      )}
    >
      {done ? <Check className="h-3 w-3" /> : n}
    </span>
  );
}

function StripeSection({
  orgId,
  detail,
}: {
  orgId: Id<"organizations">;
  detail: OrgDetail;
}) {
  const [isWorking, setIsWorking] = useState(false);
  const ensureCustomer = useAction(
    api.admin.billingStripe.ensureOrgStripeCustomer,
  );
  const startSubscription = useAction(
    api.admin.billingStripe.startOrgSubscription,
  );
  const cancelSubscription = useAction(
    api.admin.billingStripe.cancelOrgSubscription,
  );

  const run = async (fn: () => Promise<unknown>, success: string) => {
    setIsWorking(true);
    try {
      await fn();
      toast.success(success);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Stripe request failed."));
    } finally {
      setIsWorking(false);
    }
  };

  const hasActiveSubscription =
    !!detail.stripeSubscriptionId &&
    detail.stripeSubscriptionStatus !== "canceled";

  /** Why "Start monthly subscription" can't run yet — null when it can. */
  const startBlockedReason: string | null =
    detail.plan !== "subscription"
      ? "Set the plan to Subscription and save first"
      : !detail.subscriptionMonthlyExGstAudCents
        ? "Set a monthly fee and save first"
        : !detail.stripeCustomerId
          ? "Create the Stripe customer first"
          : null;

  return (
    <section className="flex flex-col gap-4 rounded-xl border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Stripe</h3>
        <StripeStatusPill
          stripeCustomerId={detail.stripeCustomerId}
          stripeSubscriptionStatus={detail.stripeSubscriptionStatus}
        />
      </div>

      {(detail.stripeCustomerId || detail.stripeSubscriptionId) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
          {detail.stripeCustomerId && (
            <StripeIdChip
              label="Customer"
              id={detail.stripeCustomerId}
              href={
                detail.stripeDashboardUrl
                  ? `${detail.stripeDashboardUrl}/customers/${detail.stripeCustomerId}`
                  : null
              }
            />
          )}
          {detail.stripeSubscriptionId && (
            <StripeIdChip
              label="Subscription"
              id={detail.stripeSubscriptionId}
              href={
                detail.stripeDashboardUrl
                  ? `${detail.stripeDashboardUrl}/subscriptions/${detail.stripeSubscriptionId}`
                  : null
              }
            />
          )}
        </div>
      )}

      {/*
        Two ordered steps. Step 2 stays visible but disabled until its
        preconditions are met, with `startBlockedReason` naming what is
        missing — a hidden button reads as a missing feature.
      */}
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <StepNumber done={!!detail.stripeCustomerId} n={1} />
          {detail.stripeCustomerId ? (
            <span className="text-sm text-muted-foreground">
              Stripe customer created.
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              disabled={isWorking}
              onClick={() =>
                run(() => ensureCustomer({ orgId }), "Stripe customer created.")
              }
            >
              Create Stripe customer
            </Button>
          )}
        </div>

        {!hasActiveSubscription && (
          <div className="flex flex-wrap items-center gap-2.5">
            <StepNumber done={false} n={2} />
            <Button
              size="sm"
              disabled={isWorking || startBlockedReason !== null}
              onClick={() =>
                run(
                  () => startSubscription({ orgId }),
                  "Subscription started — Stripe will email the first invoice.",
                )
              }
            >
              Start monthly subscription
            </Button>
            {startBlockedReason && (
              <span className="text-xs text-muted-foreground">
                {startBlockedReason}
              </span>
            )}
          </div>
        )}

        {hasActiveSubscription && (
          <div className="flex flex-wrap items-center gap-2.5">
            <StepNumber done n={2} />
            <span className="text-sm">Monthly subscription is running.</span>
            <Button
              variant="destructive"
              size="sm"
              disabled={isWorking}
              onClick={() =>
                run(
                  () => cancelSubscription({ orgId }),
                  "Subscription cancelled.",
                )
              }
            >
              Cancel subscription
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Invoices are emailed to the billing contact with 14 days to pay. GST is
        added on top of ex-GST amounts. Paid invoices flow into the charge
        ledger automatically.
      </p>
    </section>
  );
}
